//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ToastHelper, ConvertHelper, LocalizationHelper, LogHelper, CommonHelper } from '../../helpers'

import { Enums } from '../../models/common'
import { OpDistributor, OpDataTypeInGroup } from '../../models/core'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'

import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { DATA_TYPE_GROUP_SERVICE_NAME, DataTypeGroupService } from '../data-type'
import { SYSTEM_SERVICE_NAME, SystemService } from '../system'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'


//#region Public Types

export const DISTRIBUTOR_SERVICE_NAME = 'DistributorService'
export const DISTRIBUTOR_SERVICE_PROVIDER_NAME = 'DistributorServiceProvider'

//#endregion


export class DistributorService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Distributor]' }
    public static get FILE_NAME_PREFIX(): string { return 'distributors' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdDistributor }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Distributor }

    //#endregion


    //#region Ctor

    public constructor(
        private dataTypeGroupService: DataTypeGroupService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Get

    public getDistributor(idDistributor: number) {
        return this.userSessionService
            .getDataProvider().Distributor
            .getDistributor(idDistributor)
            .then((distributor: OpDistributor) => {
                if (distributor != null) {
                    return distributor
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(DistributorService.LOG_PREFIX, `Unable to load distributor for id ${idDistributor}`, error)

                return null
            })
    }

    //#endregion


    //#region Parameters

    public getBasicParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            DistributorService.SIMAX_MODULE_NAME,
            {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor
            }
        )
    }

    //#endregion


    //#region Save

    public saveDistributor(distributorToSave: OpDistributor, isBulkSave: boolean = false): JQueryPromise<OpDistributor> {
        return this.userSessionService
            .getDataProvider().Distributor
            .saveDistributor(distributorToSave)
            .then((savedObject: OpDistributor) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, DistributorService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.distributorSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, DistributorService.SIMAX_MODULE_NAME))
                LogHelper.warn(DistributorService.LOG_PREFIX, `Unable to save distributor with id ${distributorToSave.idDistributor}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion


    //#region Delete

    public deleteDistributor(selectedItemId: number): JQueryPromise<void> {
        const toDelete: OpDistributor = new OpDistributor(null)

        toDelete.idDistributor = selectedItemId

        return this.userSessionService
            .getDataProvider().Distributor
            .deleteDistributor(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, DistributorService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, DistributorService.SIMAX_MODULE_NAME))
                LogHelper.warn(DistributorService.LOG_PREFIX, `Unable to delete distributor for id ${selectedItemId}`, error)
            })
    }

    //#endregion


    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            idReferenceType: DistributorService.REFERENCE_TYPE,
            moduleName: DistributorService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject,
            referenceObjectId,
        }
    }

    public openDetails(distributorId: number, pageItemsIds: number[]) {
        this.systemService.openDetails({
            moduleName: DistributorService.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: distributorId,
        })
    }

    //#endregion
}

export class DistributorServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DATA_TYPE_GROUP_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            dataTypeGroupService: DataTypeGroupService,
            systemService: SystemService,
            userSessionService: UserSessionService
        ) {
            return new DistributorService(dataTypeGroupService, systemService, userSessionService)
        }]

    //#endregion
} 